import React, { useState, useEffect } from 'react';
import { Dialog, BoxProps, Box, Grid, Button } from '@mui/material';
import ReactQuill, { Quill } from 'react-quill';
import katex from 'katex';
import Axios from 'axios';
import ImageResize from 'quill-image-resize-module-react';
import ImageUploader from 'quill-image-uploader';
import 'katex/dist/katex.min.css';
import 'react-quill/dist/quill.snow.css';
import './styles.css';
import EquationAddModal from './EquationAddModal';

import { formats, modules } from '../../../../../../constants';
import {
  getAuthTokenFromLocalStorage,
  getUniqueImageFileName,
} from '../../../../../../helper';

export interface ISignImage {
  contentType: string;
  objectName: string;
  signedUrl: string;
}

Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/imageUploader', ImageUploader);

export interface IAddDetailModal {
  open: boolean;
  onClose: () => void;
  details: string;
  updateDetails: (html: string) => void;
}

const containerStyles: BoxProps['sx'] = {
  backgroundColor: 'background.default',
  p: '15px',
  minWidth: { xs: 'unset', sm: '90vw' },
};
const editorContainerStyles: React.CSSProperties = {
  overflowY: 'auto',
  maxHeight: '60vh',
  marginTop: '15px',
  marginBottom: '15px',
};
const buttonContainer: React.CSSProperties = {
  borderTop: '1px solid lightgray',
  paddingTop: '15px',
};

const AddDetailModal: React.FC<IAddDetailModal> = ({
  open,
  onClose,
  details,
  updateDetails,
}) => {
  // LOCAL STATES
  const [value, setValue] = useState(details);
  const [openStatus, setOpenStatus] = useState(false);

  // REF
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let reactQuillRef: any = null;

  // USE EFFECT
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).katex = katex;
  }, []);

  const handleSubmit = () => {
    updateDetails(value);
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInsertFormula = (text: any) => {
    const quillRef = reactQuillRef.getEditor();
    const range = quillRef.getSelection();
    const position = range ? range.index : 0;
    quillRef.insertEmbed(position, 'formula', text, 'user');
    const cursor = quillRef.getSelection();
    const cursorPosition = cursor ? cursor.index : 1;
    quillRef.setSelection(cursorPosition + 1);
    setOpenStatus(false);
  };

  // RENDER
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: { xs: '95vw' }, m: '0' },
      }}
    >
      <Box role="presentation" sx={containerStyles}>
        <div style={editorContainerStyles}>
          <ReactQuill
            ref={(el) => {
              reactQuillRef = el;
            }}
            theme="snow"
            value={value}
            onChange={setValue}
            placeholder="Enter here"
            formats={formats}
            modules={{
              ...modules,
              toolbar: {
                ...modules.toolbar,
                handlers: {
                  formula: () => {
                    setOpenStatus(true);
                  },
                },
              },
              imageUploader: {
                upload: async (file: File) => {
                  const newFileName = getUniqueImageFileName(file.name);
                  const newFile = new File([file], newFileName, {
                    type: file.type,
                    lastModified: file.lastModified,
                  });
                  const signImageData: ISignImage = await Axios.get(
                    `${process.env.REACT_APP_TUTOPIYA_BASE_URL}/api/v1/sign_url?objectName=${newFile.name}&contentType=${newFile.type}`,
                    {
                      headers: {
                        Authorization: `Bearer ${getAuthTokenFromLocalStorage()}`,
                      },
                    }
                  ).then((res) => res.data);
                  await Axios.put(signImageData.signedUrl, newFile, {
                    headers: { 'Content-Type': signImageData.contentType },
                  });
                  return `${process.env.REACT_APP_TUTOPIYA_S3_BUCKET_URL}/${signImageData.objectName}`;
                },
              },
              imageResize: {
                parchment: Quill.import('parchment'),
                modules: ['DisplaySize', 'Toolbar'],
              },
            }}
          />
          <EquationAddModal
            openStatus={openStatus}
            close={() => setOpenStatus(false)}
            AddEquation={handleInsertFormula}
          />
        </div>
        <Box style={buttonContainer}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Grid item>
              <Button variant="contained" color="secondary" onClick={onClose}>
                Close
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={value.trim().length === 0}
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Okay
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddDetailModal;
